import React from 'react';
import {Link} from 'react-router-dom';
import Icon from '@/shared/components/Icon';
import SectionHeader from '@/shared/components/SectionHeader';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import biomatchKitBox from '@/assets/dashboard/biomatch_kit_box.png';
import {useLatestOrder} from '../hooks/useLatestOrder';
import OrderStatusTimeline, {type OrderStep} from './OrderStatusTimeline';

/**
 * "Your Latest Order" card — Nathan's v6 layout: one horizontal row,
 * [category badge + product name + biomarker count] · [product image] ·
 * [status timeline] · │ · [status badge + View Results].
 */

// Order-stage status badge — a stage-colored dot + label, intentionally NOT a
// button (no fill/border). Colors come from the design-system UI-semantic
// palette (theme.css): neutral → info → warning → success as the order
// progresses. Tenant-overridable, same family as the "Verified" badge.
const STAGE_BADGE = {
    ordered: {label: 'Order Placed', dot: 'bg-mld-muted-text', text: 'text-mld-muted-text'},
    received: {label: 'Sample Received', dot: 'bg-mld-info', text: 'text-mld-info'},
    processed: {label: 'Labs Processed', dot: 'bg-mld-warning', text: 'text-mld-warning'},
    ready: {label: 'Results Ready', dot: 'bg-mld-success', text: 'text-mld-success'},
} as const;

const YourLatestOrderCard: React.FC = () => {
    const {order, isLoading} = useLatestOrder();
    if (isLoading || !order) return null;

    const lab = order.labProgress;
    const steps: OrderStep[] = [
        {label: 'Order Date', date: order.orderDate, completed: true},
        {label: 'Sample Received', date: lab?.sampleReceivedDate ?? null, completed: lab?.sampleReceivedDate != null},
        {label: 'Labs Processed', date: lab?.labsProcessedDate ?? null, completed: lab?.labsProcessedDate != null},
    ];
    const resultsReady = lab?.resultsReady === true;
    const viewResultsId = lab?.viewResultsOrderId ?? null;

    // Furthest-reached stage drives the status badge.
    const stage: keyof typeof STAGE_BADGE = resultsReady
        ? 'ready'
        : lab?.labsProcessedDate
          ? 'processed'
          : lab?.sampleReceivedDate
            ? 'received'
            : 'ordered';
    const badge = STAGE_BADGE[stage];

    // Stored product images are API-relative (/api/public-media?…) so the
    // bucket can be re-pointed without rewriting rows — rebase onto the API
    // origin before rendering. Imageless At-Home kit products (Product
    // Builder rows ship without an image until ops uploads one) fall back
    // to the same branded kit-box art the BioMatch hero uses: every kit
    // arrives in that box, so it's an accurate stand-in. Non-kit products
    // without an image keep rendering imageless.
    const isKitCategory = order.product.category?.startsWith('At-Home') ?? false;
    const imageSrc = resolveMediaUrl(order.product.imageUrl) || (isKitCategory ? biomatchKitBox : null);

    return (
        <section>
            <SectionHeader title="Your Latest Purchase" />
            <div className="bg-white rounded-2xl border border-mld-panel-border shadow-sm p-6">
                <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-8">
                    {/* Product info */}
                    <div className="flex shrink-0 flex-col gap-2">
                        <span className="inline-flex w-fit items-center rounded-full border border-mld-panel-border px-3 py-1 text-[12px] font-medium text-mld-text-primary">
                            {order.product.category}
                        </span>
                        <h3 className="text-[20px] font-semibold text-mld-text-primary">{order.product.name}</h3>
                        {order.product.biomarkerCount != null && (
                            <span className="inline-flex items-center gap-1.5 text-[13px] text-mld-text-primary/70">
                                <Icon name="pencil" className="h-3.5 w-3.5 shrink-0" />
                                {order.product.biomarkerCount} Biomarkers
                            </span>
                        )}
                    </div>

                    {/* Product image */}
                    {imageSrc && (
                        <div className="shrink-0 rounded-xl border border-mld-panel-border bg-white p-2 shadow-sm">
                            <img
                                src={imageSrc}
                                alt={order.product.name}
                                className="h-24 w-40 rounded-lg object-contain"
                            />
                        </div>
                    )}

                    {/* Status timeline */}
                    <OrderStatusTimeline steps={steps} />

                    {/* Divider + status badge (shown at every stage) + results link (when ready) */}
                    <div className="flex flex-col items-start gap-3 md:ml-auto md:self-stretch md:justify-center md:border-l md:border-mld-panel-border md:pl-8">
                        <span className={`inline-flex items-center gap-2 text-[13px] font-medium ${badge.text}`}>
                            <span className={`h-2 w-2 shrink-0 rounded-full ${badge.dot}`} aria-hidden="true" />
                            {badge.label}
                        </span>
                        {viewResultsId != null && (
                            <Link
                                to={`/result/${viewResultsId}`}
                                className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-mld-text-primary">
                                View Results <Icon name="arrow-up-right-from-square" className="h-4 w-4" />
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default YourLatestOrderCard;
